import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceDot, ReferenceLine,
} from 'recharts'
import type { TeamHistory } from '../types'

/**
 * KPI definition: which stock to pull and how to present it.
 *
 * `transform` lets BVI show as $M (×19/1000), Adoption as % (×100), etc.
 * `visibleToTeam=false` means hide during play; only show in facilitator
 * debrief view (classic Sterman hidden-stock reveal).
 */
export interface KPIDef {
  key: string                // stock name as stored in snapshot.stocks
  label: string              // human-readable title
  unit: string               // "$M", "%", "" (dimensionless)
  transform?: (v: number) => number
  visibleToTeam: boolean
  // Optional axis bounds so charts don't autoscale wildly in early quarters.
  domain?: [number | 'auto', number | 'auto']
}

export const KPI_DEFS: KPIDef[] = [
  // Visible during play
  { key: 'Business Value Index', label: 'Business Value', unit: '$M',
    transform: v => v * 19 / 1000, visibleToTeam: true,
    domain: [0, 'auto'] },
  { key: 'Workforce Adoption', label: 'Adoption', unit: '%',
    transform: v => v * 100, visibleToTeam: true,
    domain: [0, 100] },
  { key: 'Leadership Confidence', label: 'Leadership Confidence', unit: '',
    visibleToTeam: true, domain: [0, 100] },
  { key: 'Deployed Use Cases', label: 'Deployed Use Cases', unit: '',
    visibleToTeam: true, domain: [0, 'auto'] },
  { key: 'Active Use Case Pipeline', label: 'Active Pipeline', unit: '',
    visibleToTeam: true, domain: [0, 'auto'] },
  { key: 'Monthly Budget Stock', label: 'Quarterly Budget', unit: '$K/qtr',
    transform: v => v * 3, visibleToTeam: true, domain: [0, 'auto'] },

  // Hidden during play; revealed at debrief
  { key: 'Resistance Stock', label: 'Resistance', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
  { key: 'Change Capacity', label: 'Change Capacity', unit: '',
    visibleToTeam: false, domain: [0, 100] },
  { key: 'Talent Skills Capital', label: 'Talent Skills Capital', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
  { key: 'Governance Maturity', label: 'Governance Maturity', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
  { key: 'Leadership Capability', label: 'Leadership Capability', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
  { key: 'AI Capability', label: 'AI Capability', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
  { key: 'Risk Stock', label: 'Risk Stock', unit: '',
    visibleToTeam: false, domain: [0, 'auto'] },
]

// Deterministic pleasant palette for overlaying teams.
export const TEAM_COLORS = [
  '#1F4E79', // brand blue
  '#C1121F', // crimson
  '#2A9D8F', // teal
  '#E9C46A', // mustard
  '#8338EC', // violet
  '#F4A261', // tangerine
  '#264653', // slate
  '#52B788', // green
]

export function teamColor(teamIndex: number): string {
  return TEAM_COLORS[teamIndex % TEAM_COLORS.length]
}

interface Props {
  kpi: KPIDef
  /** Teams to plot. If a single team is passed, the line is in brand color. */
  teams: TeamHistory[]
  /** Team ordering for stable colors. Usually the parent's team_order array. */
  teamOrder: string[]
  /** Current quarter cursor (draws a vertical line). Omit to hide. */
  currentQuarter?: number
  /** If true, draw a small dot at each quarter where this team fired events.
   *  Only used in multi-team debrief mode. */
  showEventMarkers?: boolean
  height?: number
}

export default function KPIChart({
  kpi, teams, teamOrder, currentQuarter,
  showEventMarkers = false, height = 180,
}: Props) {
  // Unify all teams' quarters into a single x-axis domain. Recharts works
  // best with a single "data" array keyed by the x-axis value, but since
  // different teams may have different quarter counts, we plot each team
  // as its own <Line> using the team's own data.
  const maxQ = Math.max(
    1,
    ...teams.map(t => t.snapshots.at(-1)?.quarter ?? 0),
  )

  // Turn each team's snapshots into {q, value} pairs.
  const series = teams.map(t => ({
    team: t,
    color: teamColor(teamOrder.indexOf(t.team_id)),
    data: t.snapshots.map(s => ({
      q: s.quarter,
      value: apply(kpi, s.stocks[kpi.key]),
      hadEvent: s.events_fired.length > 0,
    })),
  }))

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-semibold text-slate-700">{kpi.label}</span>
        <span className="text-xs text-slate-400">{kpi.unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart margin={{ top: 5, right: 10, left: 0, bottom: 18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
          <XAxis
            type="number"
            dataKey="q"
            domain={[1, maxQ]}
            allowDecimals={false}
            tick={{ fontSize: 11, fill: '#666' }}
            label={{ value: 'Quarter', position: 'insideBottom', offset: -10, fontSize: 11, fill: '#888' }}
          />
          <YAxis
            domain={kpi.domain ?? [0, 'auto']}
            tick={{ fontSize: 11, fill: '#666' }}
            width={40}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, border: '1px solid #ccc' }}
            formatter={(v) => [formatValue(Number(v), kpi), kpi.label]}
            labelFormatter={(q) => `Q${q}`}
          />
          {currentQuarter !== undefined && currentQuarter > 0 && (
            <ReferenceLine
              x={currentQuarter}
              stroke="#999"
              strokeDasharray="4 2"
              label={{ value: 'now', position: 'top', fill: '#999', fontSize: 10 }}
            />
          )}
          {series.map((s) => (
            <Line
              key={s.team.team_id}
              type="monotone"
              data={s.data}
              dataKey="value"
              name={s.team.team_name}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          {showEventMarkers && series.flatMap(s =>
            s.data.filter(d => d.hadEvent).map((d, i) => (
              <ReferenceDot
                key={`${s.team.team_id}-${d.q}-${i}`}
                x={d.q}
                y={d.value}
                r={3}
                fill={s.color}
                stroke="#fff"
                strokeWidth={1}
              />
            ))
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function apply(kpi: KPIDef, raw: number | undefined): number {
  if (raw === undefined || raw === null) return 0
  return kpi.transform ? kpi.transform(raw) : raw
}

function formatValue(v: number, kpi: KPIDef): string {
  if (kpi.unit === '%') return `${v.toFixed(0)}%`
  if (kpi.unit === '$M') return `$${v.toFixed(2)}M`
  if (kpi.unit === '$K/qtr') return `$${Math.round(v).toLocaleString('en-US')}K/qtr`
  return v.toFixed(1)
}
